import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { useTRPC } from "@/lib/trpc";
import { useSuspenseQueryDeferred } from "@/lib/useSuspenseQueryDeferred";
import { type TaskQueryParams, taskQueryParamsSchema } from "@/types/list";

export const useTasksQueryOptions = (params: TaskQueryParams) => {
    const trpc = useTRPC();
    const opts = trpc.task.list.queryOptions(params);
    return opts;
};

export const useTasks = (params: TaskQueryParams) => {
    const trpc = useTRPC();
    const opts = trpc.task.list.queryOptions(params);
    const q = useQuery(opts);

    return q;
};

export const useActivityCount = ({ id }: { id: number }) => {
    const trpc = useTRPC();
    const opts = trpc.task.getActivityCount.queryOptions(
        { id },
        {
            refetchOnWindowFocus: true,
            refetchInterval: 30000, // 30 seconds
        }
    );
    const q = useQuery(opts);
    return q;
};

export const useSubTasksCount = ({ id }: { id: number }) => {
    const trpc = useTRPC();
    const opts = trpc.task.getSubTasksCount.queryOptions({ id });
    const q = useQuery(opts);
    return q;
};

export const useSuspenseTasks = (params: TaskQueryParams) => {
    const result = taskQueryParamsSchema.safeParse(params);
    if (!result.success) {
        throw new Error(`Invalid task query params in useSuspenseTasks: ${result.error.message}`);
    }
    const trpc = useTRPC();
    const opts = trpc.task.list.queryOptions(params);
    const q = useSuspenseQuery(opts);

    return q;
};

/**
 * This is the query used for the main task list
 */

export const useSuspenseDeferredTasks = (params: TaskQueryParams) => {
    const result = taskQueryParamsSchema.safeParse(params);
    if (!result.success) {
        throw new Error(`Invalid task query params in useSuspenseTasks: ${result.error.message}`);
    }
    const trpc = useTRPC();
    const opts = trpc.task.list.queryOptions(params, {
        refetchOnWindowFocus: false,
    });
    const q = useSuspenseQueryDeferred(opts);

    return q;
};

export const useSuspenseTaskDescription = ({ id }: { id: number }) => {
    const trpc = useTRPC();
    const opts = trpc.task.getDescription.queryOptions({ id });
    const q = useSuspenseQuery(opts);
    return q;
};

export const useTaskQueryOptions = ({ id }: { id: number }) => {
    const trpc = useTRPC();
    const opts = trpc.task.get.queryOptions({ id });
    return opts;
};

export const useTask = ({ id }: { id: number }) => {
    const opts = useTaskQueryOptions({ id });
    const q = useQuery(opts);
    return q;
};

export const useSuspenseTask = ({ id }: { id: number }) => {
    const trpc = useTRPC();
    const opts = trpc.task.get.queryOptions({ id });
    return useSuspenseQuery(opts);
};
